import { getTransporterApiBaseUrl } from "./api-base";
import type { TransporterProfileDto } from "./fetch-transporter-delivery-ops";

export class TransporterBankLinkExchangeError extends Error {
  readonly httpStatus: number;
  readonly apiError?: string;
  readonly detail?: string;

  constructor(httpStatus: number, apiError?: string, detail?: string) {
    const suffix = apiError ? `|${apiError}` : "";
    super(`transporter_bank_link_exchange_http_${httpStatus}${suffix}`);
    this.name = "TransporterBankLinkExchangeError";
    this.httpStatus = httpStatus;
    this.apiError = apiError;
    this.detail = detail;
  }
}

export function formatTransporterBankLinkExchangeError(err: unknown): string {
  if (!(err instanceof Error)) {
    return "Could not save your bank link. Try again, or check that the API is running.";
  }
  if (err.message === "not_signed_in") {
    return "Sign in again — your session token was missing.";
  }
  if (err instanceof TransporterBankLinkExchangeError) {
    const api = err.apiError;
    if (api === "entity_phone_not_e164") {
      return "Your transporter profile needs a phone number in international format (e.g. +15551234567) for ACH. Update Organization & contact, then try linking again.";
    }
    if (api === "plaid_no_accounts" || api === "plaid_account_not_found") {
      return "Plaid did not return a usable account. Close the bank picker and use “Link bank with Plaid” again; choose a checking account when prompted.";
    }
    if (api === "plaid_exchange_failed") {
      return "Plaid rejected the handoff after Link. Try again; confirm PLAID_CLIENT_ID, PLAID_SECRET, and PLAID_ENV on the API match your Plaid dashboard.";
    }
    if (api === "straddle_bank_link_failed") {
      const hint =
        "Confirm STRADDLE_* and PLAID_* on the API, and that the Straddle processor is enabled in Plaid for processor tokens.";
      if (err.detail != null && err.detail.trim().length > 0) {
        return `Bank link failed: ${err.detail.trim()} ${hint}`;
      }
      return `Bank link failed after Plaid. ${hint}`;
    }
    if (api === "forbidden") {
      return "You are not allowed to link a bank for this transporter.";
    }
    if (api === "invalid_body") {
      return "The request to save the bank link was invalid. Try again from the Link button.";
    }
    if (err.httpStatus === 401) {
      return "The API rejected your sign-in while saving the bank (401). Sign in again.";
    }
    if (err.httpStatus === 404) {
      return "Transporter not found while saving the bank link.";
    }
    if (err.httpStatus === 502) {
      const d = err.detail?.trim();
      return d && d.length > 0
        ? `Could not save your bank (502). ${d}`
        : "The API could not finish saving your bank (502). Check API logs and STRADDLE_* / PLAID_* env vars.";
    }
    if (api) {
      return `Could not save bank link (${api}). Try again or check API logs.`;
    }
    return `Could not save bank link (HTTP ${err.httpStatus}). Try again.`;
  }
  if (err.message.includes("Failed to fetch") || err.name === "TypeError") {
    return "Could not reach the API while saving your bank (network error).";
  }
  return err.message || "Could not save your bank link. Try again.";
}

export function formatTransporterBankLinkStepError(err: unknown): string {
  if (!(err instanceof Error)) {
    return "Could not start Plaid. Check that the API is running and try again.";
  }
  if (err.message === "not_signed_in") {
    return "Sign in again — your session token was missing.";
  }
  const m = /^bank_link_token_http_(\d+)$/.exec(err.message);
  const code = m ? Number(m[1]) : NaN;
  if (!Number.isNaN(code)) {
    if (code === 401) {
      return "The API rejected your sign-in token (401).";
    }
    if (code === 403) {
      return "Not allowed to create a Plaid link token for this transporter.";
    }
    if (code === 404) {
      return "Transporter was not found (404).";
    }
    if (code === 502) {
      return "Plaid refused to create a link token (502). On the API, set PLAID_CLIENT_ID, PLAID_SECRET, and PLAID_ENV=sandbox (or production).";
    }
    if (code >= 500) {
      return `API error while starting Plaid (HTTP ${code}).`;
    }
    return `Request failed (HTTP ${code}).`;
  }
  if (err.message.includes("Failed to fetch") || err.name === "TypeError") {
    return "Could not reach the API (network error).";
  }
  return err.message || "Could not start Plaid.";
}

export type TransporterBankLinkTokenResponse = {
  linkToken: string;
  expiration: string | null;
};

async function authPost(
  getToken: () => Promise<string | null>,
  path: string,
  body: Record<string, unknown> | null,
): Promise<Response> {
  const token = await getToken();
  if (!token) {
    throw new Error("not_signed_in");
  }
  const base = getTransporterApiBaseUrl();
  const url = base ? `${base}${path}` : path;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (body != null) {
    headers["Content-Type"] = "application/json";
  }
  return fetch(url, {
    method: "POST",
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

export async function createTransporterBankLinkToken(
  getToken: () => Promise<string | null>,
  transporterId: string,
): Promise<TransporterBankLinkTokenResponse> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/plaid/bank-link-token`;
  const res = await authPost(getToken, path, null);
  if (!res.ok) {
    throw new Error(`bank_link_token_http_${res.status}`);
  }
  return (await res.json()) as TransporterBankLinkTokenResponse;
}

export async function exchangeTransporterBankPublicToken(
  getToken: () => Promise<string | null>,
  transporterId: string,
  publicToken: string,
  accountId?: string,
): Promise<TransporterProfileDto> {
  const path = `/api/v1/transporters/${encodeURIComponent(transporterId)}/plaid/bank-link-exchange`;
  const res = await authPost(getToken, path, {
    publicToken,
    ...(accountId != null && accountId.length > 0 ? { accountId } : {}),
  });
  if (!res.ok) {
    let apiError: string | undefined;
    let detail: string | undefined;
    try {
      const j = (await res.json()) as { error?: unknown; detail?: unknown };
      if (typeof j.error === "string" && j.error.length > 0) {
        apiError = j.error;
      }
      if (typeof j.detail === "string" && j.detail.length > 0) {
        detail = j.detail;
      }
    } catch {
      /* non-JSON */
    }
    throw new TransporterBankLinkExchangeError(res.status, apiError, detail);
  }
  return (await res.json()) as TransporterProfileDto;
}
